/**
 * YelpFinder — capture leads from Yelp (Apify epctex/yelp-business-api).
 * Search by term + location; businesses without a website are auto-funnelled
 * into the No-Website pipeline (Smart Validator 2.0 applies there).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Search, Star, Phone, MapPin, Globe, XCircle, CheckCircle2,
  ExternalLink, Send, Landmark,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import ClaireHelpButton from "../components/ClaireHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function YelpFinder() {
  const [term, setTerm] = useState("");
  const [location, setLocation] = useState("Orlando, FL");
  const [maxItems, setMaxItems] = useState(20);
  const [sortBy, setSortBy] = useState("reviewCount");
  const [searching, setSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [siteFilter, setSiteFilter] = useState("");
  const [sendingIds, setSendingIds] = useState({});

  const loadStats = () =>
    axios.get(`${API}/yelp/stats`).then(({ data }) => setStats(data)).catch(() => {});
  const loadLeads = (f) => {
    const params = { limit: 200 };
    const v = f !== undefined ? f : siteFilter;
    if (v === "no") params.has_website = false;
    if (v === "yes") params.has_website = true;
    return axios.get(`${API}/yelp/leads`, { params })
      .then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  };
  useEffect(() => { loadStats(); loadLeads(); }, []);
  useEffect(() => { loadLeads(siteFilter); }, [siteFilter]); // eslint-disable-line

  const runSearch = async () => {
    if (!term.trim() || !location.trim()) {
      toast.error("Informe termo e cidade"); return;
    }
    setSearching(true);
    try {
      const { data } = await axios.post(`${API}/yelp/search`, {
        term: term.trim(), location: location.trim(),
        max_items: Number(maxItems), sort_by: sortBy,
      });
      toast.success(
        `${data.fetched} negócios · ${data.inserted} novos · ${data.no_website} sem website (${data.funnel_inserted} enviados ao funil)`);
      loadStats(); loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na busca Yelp");
    } finally { setSearching(false); }
  };

  const sendToCrm = async (l) => {
    setSendingIds((s) => ({ ...s, [l.id]: true }));
    try {
      const { data } = await axios.post(`${API}/yelp/to-crm`, { yelp_lead_id: l.id });
      toast.success(data.duplicate ? "Lead já existia no CRM" : "Lead enviado ao CRM Kanban");
      loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar ao CRM");
    } finally { setSendingIds((s) => ({ ...s, [l.id]: false })); }
  };

  return (
    <div className="flex min-h-screen bg-[var(--al-bg)]">
      <Sidebar />
      <div className="flex-1 p-6 lg:p-8 max-w-[1200px]">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <div>
            <h1 className="text-2xl font-bold text-[var(--al-text)] flex items-center gap-2">
              Yelp Lead Finder
              <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-[#D32323]/15 text-[#D32323]">YELP</span>
            </h1>
            <p className="text-[12.5px] text-[var(--al-text-muted)] mt-1">
              Busque negócios no Yelp por categoria e cidade. Os que não têm website
              entram automaticamente no funil <strong>Leads Sem Website</strong>.
            </p>
          </div>
          <ClaireHelpButton question="Como funciona o Yelp Lead Finder — buscar negócios, enviar ao CRM e o funil sem website?" />
        </div>

        <div className="mt-4 rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] font-semibold text-[var(--al-text-muted)] mb-1">Termo / categoria</label>
            <input data-testid="yelp-term-input" value={term} onChange={(e) => setTerm(e.target.value)}
              placeholder="plumber, dentist, restaurant..." className="al-input h-10 text-[13px] w-full" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] font-semibold text-[var(--al-text-muted)] mb-1">Cidade / região</label>
            <input data-testid="yelp-location-input" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="Orlando, FL" className="al-input h-10 text-[13px] w-full" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--al-text-muted)] mb-1">Máx</label>
            <select data-testid="yelp-max-select" value={maxItems} onChange={(e) => setMaxItems(e.target.value)}
              className="al-input h-10 text-[13px]">
              {[10, 20, 30, 40].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[var(--al-text-muted)] mb-1">Ordenar</label>
            <select data-testid="yelp-sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="al-input h-10 text-[13px]">
              <option value="reviewCount">Mais reviews</option>
              <option value="rating">Melhor nota</option>
              <option value="recommended">Recomendados</option>
            </select>
          </div>
          <button data-testid="yelp-search-btn" onClick={runSearch} disabled={searching}
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#D32323] hover:bg-[#B91C1C] text-white text-[13px] font-semibold disabled:opacity-60">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {searching ? "Buscando..." : "Buscar no Yelp"}
          </button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <StatCard title="Total capturado" value={stats.total} />
            <StatCard title="Sem website 🔥" value={stats.no_website} accent="text-emerald-400" />
            <StatCard title="Com website" value={stats.with_website} />
            <StatCard title="Com telefone" value={stats.with_phone} accent="text-[#3B82F6]" />
          </div>
        )}

        <div className="flex items-center gap-2 mt-5 mb-3">
          <select data-testid="yelp-site-filter" value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)} className="al-input h-9 text-[12.5px] min-w-[190px]">
            <option value="">Todos</option>
            <option value="no">🔥 Sem website</option>
            <option value="yes">Com website</option>
          </select>
          <span className="text-[11px] text-[var(--al-text-muted)]">{leads.length} negócios</span>
        </div>

        {leads.length === 0 ? (
          <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-10 text-center text-[var(--al-text-muted)]">
            Nenhum negócio capturado ainda. Faça uma busca acima.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {leads.map((l) => (
              <div key={l.id} data-testid={`yelp-lead-${l.id}`}
                className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 hover:border-[#D32323]/40 transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[var(--al-text)] text-[14px]">{l.company_name}</span>
                      {l.rating != null && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#F59E0B]">
                          <Star className="w-3 h-3 fill-[#F59E0B]" /> {l.rating}
                          <span className="text-[var(--al-text-muted)] font-normal">({l.review_count})</span>
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[var(--al-text-muted)] mt-0.5">
                      {(l.categories || []).slice(0, 3).join(" · ")}{l.price_range ? ` · ${l.price_range}` : ""}
                    </div>
                    {l.phone && (
                      <div className="flex items-center gap-1.5 text-[12px] text-[var(--al-text)] mt-1.5">
                        <Phone className="w-3 h-3" /> {l.phone}
                      </div>
                    )}
                    {l.address && (
                      <div className="flex items-center gap-1.5 text-[11px] text-[var(--al-text-muted)] mt-0.5">
                        <MapPin className="w-3 h-3 shrink-0" /> {String(l.address).split("\n").join(", ")}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {l.has_website ? (
                        <a href={l.website} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#3B82F6] hover:underline">
                          <Globe className="w-3 h-3" /> <CheckCircle2 className="w-3 h-3 text-emerald-500" /> Website
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">
                          <XCircle className="w-3 h-3" /> SEM WEBSITE → funil
                        </span>
                      )}
                      <a href={l.yelp_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[#D32323] hover:underline">
                        <ExternalLink className="w-3 h-3" /> Yelp
                      </a>
                      {l.sent_to_crm && (
                        <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500">NO CRM</span>
                      )}
                    </div>
                  </div>
                  <button data-testid={`yelp-tocrm-${l.id}`} onClick={() => sendToCrm(l)}
                    disabled={!!sendingIds[l.id] || l.sent_to_crm}
                    className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[11px] font-semibold disabled:opacity-50">
                    {sendingIds[l.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    CRM
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 text-[11.5px] text-[var(--al-text-muted)] flex items-start gap-2">
          <Landmark className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Negócios sem website vão automaticamente para <strong>Leads Sem Website</strong>,
            onde o Smart Validator 2.0 confirma a ausência real de presença digital antes de
            classificar como oportunidade.
          </span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, accent = "text-[var(--al-text)]" }) {
  return (
    <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
      <div className="text-[var(--al-text-muted)] text-[10.5px] uppercase tracking-wide mb-1">{title}</div>
      <div className={`text-3xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}
